import { useEffect, useRef } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

/**
 * useSEO — injects Schema.org JSON-LD only.
 *
 * Title, meta description, keywords, canonical, OG, and Twitter Card tags
 * are managed statically in index.html and by platform build settings.
 * Runtime head mutation for those tags is intentionally removed to avoid
 * conflicts with setting-managed head tags.
 */
export const useSEO = ({ schema }: SEOProps) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!schema) return;

    const schemaId = 'seo-schema-jsonld';
    let scriptTag = document.getElementById(schemaId) as HTMLScriptElement | null;

    if (scriptTag) {
      // Update existing script tag — avoids duplicate <script> elements
      scriptTag.textContent = JSON.stringify(schema);
    } else {
      scriptTag = document.createElement('script');
      scriptTag.id = schemaId;
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptTag);
    }

    // Store cleanup ref to avoid stale closure issues
    cleanupRef.current = () => {
      const existing = document.getElementById(schemaId);
      if (existing) {
        existing.remove();
      }
    };

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [schema]);
};